import { Button, Upload, Collapse, Space, Typography } from "antd";
import { UploadOutlined, UserOutlined, ApiOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { DetailedWithdrawRequestRow } from "../types";

const { Text } = Typography;

interface CustomerBondFilesSectionProps {
  detailedWithdrawData: DetailedWithdrawRequestRow[];
  customerBondFiles: Record<string, { purchaseData: Record<string, unknown>[]; txHistoryData: Record<string, unknown>[] }>;
  customerDataLoading: boolean;
  excludedUsers: Set<string>;
  onFetchFromAPI: () => void;
  onCustomerPurchaseFile: (regNumber: string, file: File) => void;
  onCustomerTxHistoryFile: (regNumber: string, file: File) => void;
  onClearAll: () => void;
  onToggleExclude: (regNumber: string) => void;
}

export const CustomerBondFilesSection = ({
  detailedWithdrawData,
  customerBondFiles,
  customerDataLoading,
  excludedUsers,
  onFetchFromAPI,
  onCustomerPurchaseFile,
  onCustomerTxHistoryFile,
  onClearAll,
  onToggleExclude,
}: CustomerBondFilesSectionProps) => {
  if (detailedWithdrawData.length === 0) return null;

  // Show ALL unique customers, not just bond customers
  const allCustomers = [...new Map(
    detailedWithdrawData
      .map(r => [r.regNumber.trim(), { regNumber: r.regNumber.trim(), email: r.email.toLowerCase().trim() }])
  ).values()];

  const activeCount = allCustomers.filter(c => !excludedUsers.has(c.regNumber)).length;

  return (
    <Collapse style={{ marginTop: 16 }}>
      <Collapse.Panel
        key="customers"
        header={
          <span style={{ fontSize: 13, fontWeight: 500, color: "#262626" }}>
            8. Харилцагчдын мэдээлэл
          </span>
        }
        extra={
          <Button
            icon={<ApiOutlined />}
            onClick={(e) => { e.stopPropagation(); onFetchFromAPI(); }}
            loading={customerDataLoading}
            type="primary"
          >
            API-аас татах
          </Button>
        }
      >
      <Collapse>
        {allCustomers.map(({ regNumber, email }) => {
          const customerRows = detailedWithdrawData.filter(
            r => r.regNumber.trim() === regNumber
          );
          const bondRows = customerRows.filter(r => r.assetType === "Бонд");
          const stockRows = customerRows.filter(r => r.assetType !== "Бонд");
          const firstName = customerRows[0]?.firstName || "";
          const lastName = customerRows[0]?.lastName || "";
          const hasFiles = customerBondFiles[regNumber];
          const purchaseCount = hasFiles?.purchaseData?.length || 0;
          const txCount = hasFiles?.txHistoryData?.length || 0;
          const isExcluded = excludedUsers.has(regNumber);

          return (
            <Collapse.Panel
              key={regNumber}
              header={
                <span style={{ opacity: isExcluded ? 0.4 : 1 }}>
                  <UserOutlined style={{ marginRight: 8 }} />
                  {lastName} {firstName} ({email})
                  {bondRows.length > 0 && ` - ${bondRows.length} бонд`}
                  {stockRows.length > 0 && ` - ${stockRows.length} хувьцаа`}
                  {isExcluded && (
                    <Text type="danger" style={{ marginLeft: 8, fontSize: 12 }}>
                      [Хасагдсан]
                    </Text>
                  )}
                  {hasFiles && !isExcluded && (
                    <Text type={purchaseCount > 0 || txCount > 0 ? "success" : "warning"} style={{ marginLeft: 8, fontSize: 12 }}>
                      ХА:{purchaseCount} ГТ:{txCount}
                    </Text>
                  )}
                </span>
              }
              extra={
                <Button
                  size="small"
                  danger={!isExcluded}
                  type={isExcluded ? "primary" : "default"}
                  icon={isExcluded ? <PlusOutlined /> : <DeleteOutlined />}
                  onClick={(e) => { e.stopPropagation(); onToggleExclude(regNumber); }}
                >
                  {isExcluded ? "Нэмэх" : "Хасах"}
                </Button>
              }
            >
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <div>
                  <Text style={{ fontSize: 12 }}>Худалдан авалт (Захиалгын биелэлт):</Text>
                  <Upload
                    accept=".xlsx,.xls"
                    showUploadList={false}
                    beforeUpload={(file) => { onCustomerPurchaseFile(regNumber, file); return false; }}
                  >
                    <Button size="small" icon={<UploadOutlined />}>
                      {purchaseCount > 0 ? `${purchaseCount} мөр` : "Файл сонгох"}
                    </Button>
                  </Upload>
                </div>
                <div>
                  <Text style={{ fontSize: 12 }}>Гүйлгээний түүх:</Text>
                  <Upload
                    accept=".xlsx,.xls"
                    showUploadList={false}
                    beforeUpload={(file) => { onCustomerTxHistoryFile(regNumber, file); return false; }}
                  >
                    <Button size="small" icon={<UploadOutlined />}>
                      {txCount > 0 ? `${txCount} мөр` : "Файл сонгох"}
                    </Button>
                  </Upload>
                </div>
              </Space>
            </Collapse.Panel>
          );
        })}
      </Collapse>
      {Object.keys(customerBondFiles).length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Text type="success">
            ✓ {Object.keys(customerBondFiles).length} харилцагчийн файл оруулсан
          </Text>
          <Button size="small" danger style={{ marginLeft: 8 }} onClick={onClearAll}>
            Бүгдийг цэвэрлэх
          </Button>
        </div>
      )}
      </Collapse.Panel>
    </Collapse>
  );
};
